import { getTelAreaCodes } from "@/services";
import useReq from "@/utils/useReq";
import { useMemoizedFn } from "ahooks";
import { type InputProps } from "antd";
import classNames from "classnames";
import { uniqBy } from "lodash";
import { useEffect, useMemo, useState } from "react";
import FilterSelect from "../FilterSelect";
import HBInput from "../HBInput";
import styles from "./index.module.less";

const defaultAreaCodes = [
  { value: "+86", label: "+86", optionLabel: "中国大陆 +86" },
  { value: "+852", label: "+852", optionLabel: "中国香港 +852" },
  { value: "+853", label: "+853", optionLabel: "中国澳门 +853" },
  { value: "+886", label: "+886", optionLabel: "中国台湾地区 +886" },
];

interface IOption {
  value: string;
  label: string;
  optionLabel: string;
}

interface IValue {
  value: string;
  areaCode: string;
  number: string;
}

type IProps = InputProps & {
  value?: IValue;
  onChange?: (value: IValue) => void;
  placeholder?: string;
  autofillCheck?: boolean;
  noWrapper?: boolean;
  wrapClass?: string;
};

export function TelInput(props: IProps) {
  const { value, ...rest } = props;

  const [areaCode, setAreaCode] = useState(defaultAreaCodes[0]);

  const onAreaCodeChange = useMemoizedFn((option) => {
    setAreaCode(option);
    props.onChange?.({
      areaCode: option.value,
      value:
        option.value !== "+86" ? option.label + value?.number : value?.number,
      number: value?.number,
    });
  });

  const { data: telAreaCodes } = useReq(
    async () => {
      try {
        const codes = await getTelAreaCodes();
        // 处理成数组
        return uniqBy(
          codes.data.reduce((pre, cur) => {
            return pre.concat(
              cur.list
                .filter((n) => {
                  // “缅甸、柬埔寨、斯里兰卡、越南由于有“盗刷短信”，这些地区中台会默认屏蔽掉
                  // 过滤掉中国相关的，中国使用默认固定的
                  return (
                    !n.name.includes("中国") &&
                    !n.name.includes("柬埔寨") &&
                    !n.name.includes("缅甸") &&
                    !n.name.includes("斯里兰卡")
                  );
                })
                .map((area) => {
                  const option = {
                    value: area.code,
                    label: area.code,
                    optionLabel: `${area.name} ${area.code}`,
                  };

                  if (area.code === value?.areaCode) {
                    setAreaCode(option);
                  }

                  return option;
                })
            );
          }, defaultAreaCodes),
          "optionLabel"
        ) as unknown as IOption[];
      } catch (error) {
        return defaultAreaCodes;
      }
    },
    {
      cacheKey: "telAreaCodes",
      staleTime: -1,
    }
  );

  const addonBefore = useMemo(() => {
    return (
      <div>
        <FilterSelect
          className={classNames(
            styles.areaCodeSelect,
            props.disabled && styles.disabled
          )}
          allowClear={false}
          options={telAreaCodes || defaultAreaCodes}
          optionKey="optionLabel"
          value={areaCode}
          onChange={onAreaCodeChange}
          disabled={props.disabled}
        />
      </div>
    );
  }, [areaCode, onAreaCodeChange, props.disabled, telAreaCodes]);

  const onInputChange = useMemoizedFn((e) => {
    // 限制输入数字
    let telNumber = e.target.value.replace(/\D/g, "");

    // E.164 标准 https://zh.wikipedia.org/wiki/E.164
    // 国家代码（1 到 3 位数字）
    // 用户号码（最多 12 位数字）
    // 限制位数，国内限制11位，国外限制12位
    telNumber =
      areaCode.value === "+86"
        ? telNumber.slice(0, 11)
        : telNumber.slice(0, 12);

    props.onChange?.({
      areaCode: areaCode.value,
      value: areaCode.value !== "+86" ? areaCode.label + telNumber : telNumber,
      number: telNumber,
    });
  });

  // 设置areacode值
  useEffect(() => {
    if (props.value?.areaCode && telAreaCodes) {
      setAreaCode(
        telAreaCodes?.find((item) => item.value === props.value?.areaCode)
      );
    }
  }, [props.value]);

  return (
    <HBInput
      {...rest}
      value={value?.number}
      className={styles.telInput}
      addonBefore={addonBefore}
      onChange={onInputChange}
    />
  );
}
